import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './AdminPanel.css';
import jobService from './services/jobService';
import authService from './services/authService';
import companyService from './services/companyService';
import CompanyProfile from './components/company/CompanyProfile';
import CompanyUsers from './components/company/CompanyUsers';
import CompanySubscription from './components/company/CompanySubscription';
import CompanyStats from './components/company/CompanyStats';
import CustomModal from './components/CustomModal';
import JobSearchDropdown from './components/JobSearchDropdown';
import './CompanyBadge.css';


function AdminPanel() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('myJobs');

  // ==================== MY JOBS STATE ====================
  const [myJobs, setMyJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);

  // ==================== CREATE JOB STATE ====================
  const [jobTitle, setJobTitle] = useState('');
  const [company, setCompany] = useState('');
  const [description, setDescription] = useState('');
  const [qualifications, setQualifications] = useState('');
  const [experience, setExperience] = useState('');
  const [location, setLocation] = useState('');
  const [workMode, setWorkMode] = useState('');
  const [createdJobId, setCreatedJobId] = useState(null);

  // ==================== EDIT JOB STATE ====================
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [editJobTitle, setEditJobTitle] = useState('');
  const [editCompany, setEditCompany] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editQualifications, setEditQualifications] = useState('');
  const [editExperience, setEditExperience] = useState('');
  const [editLocation, setEditLocation] = useState('');
  const [editWorkMode, setEditWorkMode] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [isReanalyzing, setIsReanalyzing] = useState(false);
  const [reanalyzeProgress, setReanalyzeProgress] = useState('');

  // ==================== UPLOAD RESUMES STATE ====================
  const [jobId, setJobId] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [extractedData, setExtractedData] = useState(null);
  const [editedData, setEditedData] = useState([]);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadSuccessMessage, setUploadSuccessMessage] = useState('');
  const [currentStep, setCurrentStep] = useState('upload');

  // ==================== VIEW RANKINGS STATE ====================
  const [rankingsJobId, setRankingsJobId] = useState('');
  const [rankings, setRankings] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ==================== CUSTOM MODAL STATE ====================
    const [modal, setModal] = useState({
      isOpen: false,
      type: 'info',
      title: '',
      message: '',
      onConfirm: () => {},
      onCancel: () => {},
      showCancel: false,
      confirmText: 'OK',
      cancelText: 'Cancel'
    });

    // ==================== JOB SELECTION STATE ====================
    const [selectedJob, setSelectedJob] = useState(null);
    const [rankingsSelectedJob, setRankingsSelectedJob] = useState(null);

    const [companyName, setCompanyName] = useState('');
    const [companyInfo, setCompanyInfo] = useState(null); // ✅ Add company info state
    const isAdmin = authService.isAdmin();

    const API_BASE_URL = `${process.env.REACT_APP_API_URL || 'https://quiet-mechanic-477421-d0.uc.r.appspot.com/api'}`;

  // ==================== MODAL HELPER FUNCTIONS ====================
    const showModal = ({ type, title, message, onConfirm, onCancel, showCancel = false, confirmText = 'OK', cancelText = 'Cancel' }) => {
      setModal({
        isOpen: true,
        type,
        title,
        message,
        onConfirm: onConfirm || closeModal,
        onCancel: onCancel || closeModal,
        showCancel,
        confirmText,
        cancelText
      });
    };

    const closeModal = () => {
      setModal(prev => ({ ...prev, isOpen: false }));
    };

  useEffect(() => {
    loadCompanyInfo(); // ✅ Renamed for clarity
  }, []);

  const loadCompanyInfo = async () => { // ✅ Load full company info
    try {
      const data = await companyService.getProfile();
      setCompanyInfo(data); // ✅ Store full company object
      setCompanyName(data.companyName || data.company?.name || ''); // ✅ Backward compatibility
    } catch (err) {
      console.error('Failed to load company info:', err);
    }
  };

  // ==================== FETCH MY JOBS ====================
  useEffect(() => {
    if (activeTab === 'myJobs') {
      fetchMyJobs();
    }
  }, [activeTab]);

  const fetchMyJobs = async () => {
    setLoadingJobs(true);
    setError(null);
    try {
      const response = await jobService.getMyJobs();
      if (response && response.jobs) {
        setMyJobs(response.jobs);
      }
    } catch (err) {
      console.error('Error fetching my jobs:', err);
      setError('Failed to load your jobs. Please try again.');
    } finally {
      setLoadingJobs(false);
    }
  };

  // ==================== CREATE JOB HANDLER ====================
  const handleCreateJob = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const formData = new URLSearchParams();
    formData.append('title', jobTitle);
    formData.append('company', company);
    formData.append('description', description);
    if (qualifications) formData.append('requiredQualifications', qualifications);
    if (experience) formData.append('yearsOfExperience', experience);
    if (location) formData.append('location', location);
    if (workMode) formData.append('workMode', workMode);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/external/jobs`,
        formData,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      );

      setCreatedJobId(response.data.jobId);
      showModal({
        type: 'success',
        title: 'Job Created Successfully!',
        message: `Your job has been created with ID: ${response.data.jobId}`,
        onConfirm: () => {
          closeModal();

      setJobId(response.data.jobId.toString());

      setJobTitle('');
      setCompany('');
      setDescription('');
      setQualifications('');
      setExperience('');
      setLocation('');
      setWorkMode('');

      fetchMyJobs();
      setActiveTab('myJobs');
      }     // ← Close the onConfirm callback
    });
    } catch (err) {
      console.error('Error creating job:', err);
      setError(err.response?.data?.error || 'Failed to create job');
    } finally {
      setLoading(false);
    }
  };

  // ==================== EDIT JOB HANDLERS ====================
  const handleEditJob = (job) => {
    console.log('Editing job:', job); // Debug log
    setEditingJob(job);
    setEditJobTitle(job.title || '');
    setEditCompany(job.company || '');

    // Handle different field names for description
    const jobDesc = job.description || job.jobOverview || job.jobDescription || '';
    setEditDescription(jobDesc);

    // Handle qualifications - could be array or string
    let quals = '';
    if (job.requiredQualifications) {
      if (Array.isArray(job.requiredQualifications)) {
        quals = job.requiredQualifications.join(', ');
      } else {
        quals = job.requiredQualifications;
      }
    }
    setEditQualifications(quals);

    setEditExperience(job.yearsOfExperience || job.experienceLevel || '');
    setEditLocation(job.location || '');
    setEditWorkMode(job.workMode || '');
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingJob(null);
    setEditJobTitle('');
    setEditCompany('');
    setEditDescription('');
    setEditQualifications('');
    setEditExperience('');
    setEditLocation('');
    setEditWorkMode('');
    setError(null);
  };

  const handleUpdateJob = async (e) => {
    e.preventDefault();
    setIsUpdating(true);
    setError(null);

    const formData = new URLSearchParams();
    formData.append('title', editJobTitle);
    formData.append('company', editCompany);
    formData.append('description', editDescription);
    if (editQualifications) formData.append('requiredQualifications', editQualifications);
    if (editExperience) formData.append('yearsOfExperience', editExperience);
    if (editLocation) formData.append('location', editLocation);
    if (editWorkMode) formData.append('workMode', editWorkMode);

    try {
      const response = await axios.put(
        `${API_BASE_URL}/external/jobs/${editingJob.id}`,
        formData,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      );

      showModal({
        type: 'success',
        title: 'Job Updated Successfully!',
        message: 'Your job posting has been updated.',
        onConfirm: () => {
          closeModal();
          handleCloseEditModal();
          fetchMyJobs();
        }
      });
    } catch (err) {
      console.error('Error updating job:', err);
      setError(err.response?.data?.message || 'Failed to update job');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleReanalyzeCandidates = async (jobId) => {
    showModal({
      type: 'confirm',
      title: 'Re-analyze Candidates',
      message: 'Re-analyze all candidates for this job? This will update their scores based on the updated job description. This may take a few minutes.',
      showCancel: true,
      confirmText: 'OK',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          closeModal();
          setIsReanalyzing(true);
          setReanalyzeProgress('Starting re-analysis...');

          const response = await axios.post(
            `${API_BASE_URL}/external/jobs/${jobId}/reanalyze-candidates`,
            {},
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
              }
            }
          );

          if (response.data.success) {
            setReanalyzeProgress(`Complete! ${response.data.successCount} candidates re-analyzed.`);

            setTimeout(() => {
              setIsReanalyzing(false);
              showModal({
                type: 'success',
                title: 'Re-analysis Complete',
                message: `Successfully re-analyzed ${response.data.successCount} candidates.`,
                onConfirm: () => {
                  closeModal();
                  fetchMyJobs();
                }
              });
            }, 1000);
          } else {
            throw new Error(response.data.message);
          }
        } catch (err) {
          console.error('Error re-analyzing candidates:', err);
          setIsReanalyzing(false);
          showModal({
            type: 'error',
            title: 'Re-analysis Failed',
            message: err.response?.data?.message || 'Failed to re-analyze candidates.',
            onConfirm: closeModal
          });
        }
      },
      onCancel: closeModal
    });
  };

  const handleJobSelect = (job) => {
      setSelectedJob(job);
      setJobId(job.id.toString());
    };

    const handleRankingsJobSelect = (job) => {
      setRankingsSelectedJob(job);
      setRankingsJobId(job.id.toString());
    };

  // ==================== UPLOAD/EXTRACT/PROCESS HANDLERS ====================
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
    setExtractedData(null);
    setEditedData([]);
    setError(null);
    setUploadSuccess(false);
    setCurrentStep('upload');
  };

  const handleExtractResumes = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select at least one resume file');
      return;
    }

    if (!jobId) {
      setError('Please select a Job');
      return;
    }

    setIsExtracting(true);
    setError(null);
    setUploadSuccess(false);

    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => {
        formData.append('resumes', file);
      });
      formData.append('candidateNames', '');
      formData.append('candidateEmails', '');

      const response = await axios.post(
        `${API_BASE_URL}/external/jobs/${jobId}/candidates`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      );

      if (response.data.success && response.data.mode === 'EXTRACT') {
        setExtractedData(response.data.candidates);

        const initialEdited = response.data.candidates.map(candidate => ({
          index: candidate.index,
          fileName: candidate.fileName,
          name: candidate.extractedName || '',
          email: candidate.extractedEmail || '',
          error: candidate.error || null
        }));
        setEditedData(initialEdited);

        setError(null);
        setCurrentStep('extracted');
      } else {
        setError(response.data.error || 'Failed to extract resume info');
      }
    } catch (err) {
      console.error('Error extracting resume:', err);
      setError(err.response?.data?.error || 'Error extracting resume info. Please try again.');
    } finally {
      setIsExtracting(false);
    }
  };

  const handleEditData = (index, field, value) => {
    const updated = [...editedData];
    updated[index] = {
      ...updated[index],
      [field]: value
    };
    setEditedData(updated);
  };

  const handleProcessResumes = async () => {
    const emptyFields = editedData.some(
      candidate => !candidate.name.trim() || !candidate.email.trim()
    );

    if (emptyFields) {
      setError('Please fill in all candidate names and emails');
      return;
    }

    if (!jobId) {
      setError('Job ID is required');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setUploadSuccess(false);

    try {
      const formData = new FormData();

      selectedFiles.forEach((file) => {
        formData.append('resumes', file);
      });

      const names = editedData.map(d => d.name).join(',');
      const emails = editedData.map(d => d.email).join(',');

      formData.append('candidateNames', names);
      formData.append('candidateEmails', emails);

      const response = await axios.post(
        `${API_BASE_URL}/external/jobs/${jobId}/candidates`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      );

      if (response.data.success) {
        setUploadSuccess(true);
        setUploadSuccessMessage(
          `Successfully processed ${response.data.successCount} out of ${editedData.length} resumes!`
        );
        setCurrentStep('processed');

        fetchMyJobs();
      } else {
        setError(response.data.error || 'Failed to process resumes');
      }
    } catch (err) {
      console.error('Error processing resumes:', err);
      setError(err.response?.data?.error || 'Error processing resumes. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleResetUpload = () => {
    setSelectedFiles([]);
    setExtractedData(null);
    setEditedData([]);
    setError(null);
    setUploadSuccess(false);
    setCurrentStep('upload');
    setIsExtracting(false);
    setIsProcessing(false);
  };

  // ==================== VIEW RANKINGS HANDLERS ====================
  const handleViewRankings = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setRankings(null);

    try {
      const response = await axios.get(
        `${API_BASE_URL}/external/jobs/${rankingsJobId}/rankings`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      );

      setRankings(response.data);
    } catch (err) {
      console.error('Error fetching rankings:', err);
      setError(err.response?.data?.error || 'Failed to fetch rankings');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickViewRankings = async (jobIdToView) => {
    setRankingsJobId(jobIdToView.toString());
    setActiveTab('viewRankings');

    setLoading(true);
    setError(null);
    setRankings(null);

    try {
      const response = await axios.get(
        `${API_BASE_URL}/external/jobs/${jobIdToView}/rankings`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      );

      setRankings(response.data);
    } catch (err) {
      console.error('Error fetching rankings:', err);
      setError(err.response?.data?.error || 'Failed to fetch rankings');
    } finally {
      setLoading(false);
    }
  };

  const getFitColor = (percentage) => {
    if (percentage >= 80) return '#16a34a';
    if (percentage >= 60) return '#eab308';
    return '#dc2626';
  };

  return (
    <>
    <CustomModal
      isOpen={modal.isOpen}
      type={modal.type}
      title={modal.title}
      message={modal.message}
      onConfirm={modal.onConfirm}
      onCancel={modal.onCancel}
      showCancel={modal.showCancel}
      confirmText={modal.confirmText}
      cancelText={modal.cancelText}
    />
    <div className="greenhouse-app">
      {/* Top Navigation Bar */}
      <nav className="top-nav">
        <div className="nav-container">
          <div className="nav-left">
            <h1 className="app-logo">AI Recruitment System</h1>
            {/* ✅ Company Badge */}
            {companyInfo && (
              <div className="company-badge">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                  <polyline points="9 22 9 12 15 12 15 22"></polyline>
                </svg>
                <span>{companyInfo.companyName || companyInfo.company?.name}</span>
              </div>
            )}
          </div>

          <div className="nav-right">
            <button
              className="user-profile-btn"
              onClick={() => navigate('/profile')}
              title="Profile"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </button>
            <button
              className="logout-btn"
              onClick={() => {
                showModal({
                  type: 'confirm',
                  title: 'Confirm Logout',
                  message: 'Are you sure you want to logout?',
                  showCancel: true,
                  confirmText: 'Logout',
                  cancelText: 'Cancel',
                  onConfirm: () => {
                    localStorage.clear();
                    window.location.href = '/login';
                    closeModal();
                  },
                  onCancel: closeModal
                });
              }}
              title="Logout"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16 17 21 12 16 7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="main-container">
        {/* Tab Navigation */}
        <div className="tab-nav-container">
          <button
            className={`tab-nav-btn ${activeTab === 'myJobs' ? 'active' : ''}`}
            onClick={() => setActiveTab('myJobs')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
            </svg>
            My Jobs
          </button>
          <button
            className={`tab-nav-btn ${activeTab === 'createJob' ? 'active' : ''}`}
            onClick={() => setActiveTab('createJob')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Create Job
          </button>
          <button
            className={`tab-nav-btn ${activeTab === 'uploadResumes' ? 'active' : ''}`}
            onClick={() => setActiveTab('uploadResumes')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="17 8 12 3 7 8"></polyline>
              <line x1="12" y1="3" x2="12" y2="15"></line>
            </svg>
            Upload Resumes
          </button>
          <button
            className={`tab-nav-btn ${activeTab === 'viewRankings' ? 'active' : ''}`}
            onClick={() => setActiveTab('viewRankings')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 3v18h18"></path>
              <path d="m19 9-5 5-4-4-3 3"></path>
            </svg>
            View Rankings
          </button>
          {isAdmin && (
            <button
              className={`tab-nav-btn ${activeTab === 'company' ? 'active' : ''}`}
              onClick={() => setActiveTab('company')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M12 1v6m0 6v6"></path>
              </svg>
              ⚙️ Company Settings
            </button>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="alert alert-error">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            {error}
          </div>
        )}

        {/* Tab Content */}
        <div className="content-area">
          {/* MY JOBS TAB */}
          {activeTab === 'myJobs' && (
            <div className="tab-content-section">
              <div className="section-header">
                <h2>Open Job Postings</h2>
                <p className="section-description">Manage your active job listings and track candidates</p>
              </div>

              {loadingJobs ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading your jobs...</p>
                </div>
              ) : myJobs.length === 0 ? (
                <div className="empty-state-card">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                  </svg>
                  <h3>No jobs yet</h3>
                  <p>Create your first job posting to start finding candidates</p>
                  <button
                    onClick={() => setActiveTab('createJob')}
                    className="btn-primary-large"
                  >
                    Create Job Posting
                  </button>
                </div>
              ) : (
                <div className="jobs-grid-greenhouse">
                  {myJobs.map((job) => (
                    <div key={job.id} className="job-card-greenhouse">
                      <div className="job-card-top">
                        <div className="job-header-section">
                          <h3 className="job-title-gh">{job.title}</h3>
                          <span className="job-id-badge">ID: {job.id}</span>
                        </div>

                        <div className="job-meta-info">
                          <span className="meta-item">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                              <line x1="3" y1="9" x2="21" y2="9"></line>
                            </svg>
                            {job.company}
                          </span>
                          {job.location && (
                            <span className="meta-item">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                <circle cx="12" cy="10" r="3"></circle>
                              </svg>
                              {job.location}
                            </span>
                          )}
                          {job.workMode && (
                            <span className="meta-item">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                                <polyline points="9 22 9 12 15 12 15 22"></polyline>
                              </svg>
                              {job.workMode}
                            </span>
                          )}
                        </div>

                        <div className="job-status-row">
                          <span className="status-pill status-active">Active</span>
                          <span className="job-date">Created {new Date(job.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        </div>
                      </div>

                      <div className="job-stats-section">
                        <div className="stat-box-gh">
                          <span className="stat-number">{job.candidateCount || 0}</span>
                          <span className="stat-label">Candidates</span>
                        </div>
                      </div>

                      <div className="job-actions-gh">
                        <button
                          onClick={() => handleEditJob(job)}
                          className="btn-ghost"
                          title="Edit Job"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                          </svg>
                          Edit
                        </button>
                        <button
                          onClick={() => {
                            setJobId(job.id.toString());
                            setSelectedJob(job); // ✅ PRE-SELECT THE JOB
                            setActiveTab('uploadResumes');
                          }}
                          className="btn-ghost"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                            <polyline points="17 8 12 3 7 8"></polyline>
                            <line x1="12" y1="3" x2="12" y2="15"></line>
                          </svg>
                          Upload Resumes
                        </button>
                        <button
                          onClick={() => handleQuickViewRankings(job.id)}
                          className="btn-primary-gh"
                          disabled={!job.candidateCount || job.candidateCount === 0}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 3v18h18"></path>
                            <path d="m19 9-5 5-4-4-3 3"></path>
                          </svg>
                          View Rankings
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* CREATE JOB TAB */}
          {activeTab === 'createJob' && (
            <div className="tab-content-section">
              <div className="section-header">
                <h2>Create New Job Posting</h2>
                <p className="section-description">Fill in the details to create a new job listing</p>
              </div>

              <form onSubmit={handleCreateJob} className="form-greenhouse">
                <div className="form-grid-2">
                  <div className="form-field">
                    <label>Job Title *</label>
                    <input
                      type="text"
                      value={jobTitle}
                      onChange={(e) => setJobTitle(e.target.value)}
                      placeholder="e.g., Senior Java Developer"
                      required
                    />
                  </div>

                  <div className="form-field">
                    <label>Company *</label>
                    <input
                      type="text"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      placeholder="e.g., Tech Corp"
                      required
                    />
                  </div>
                </div>

                <div className="form-field">
                  <label>Job Description *</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Enter detailed job description..."
                    maxLength={5000}
                    rows={6}
                    required
                  />
                  <span className="char-count">{description.length}/5000 characters</span>
                </div>


                <div className="form-grid-2">
                  <div className="form-field">
                    <label>Required Qualifications</label>
                    <textarea
                      value={qualifications}
                      onChange={(e) => setQualifications(e.target.value)}
                      placeholder="Java, Spring Boot, AWS, Docker, Kubernetes, etc."
                      maxLength={5000}
                      rows={3}
                    />
                    <span className="char-count">{qualifications.length}/5000 characters</span>
                  </div>

                  <div className="form-field">
                    <label>Years of Experience</label>
                    <input
                      type="text"
                      value={experience}
                      onChange={(e) => setExperience(e.target.value)}
                      placeholder="e.g., 5+ years"
                    />
                  </div>
                </div>

                <div className="form-grid-2">
                  <div className="form-field">
                    <label>Location</label>
                    <input
                      type="text"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      placeholder="e.g., New York, NY"
                    />
                  </div>

                  <div className="form-field">
                    <label>Work Mode</label>
                    <select value={workMode} onChange={(e) => setWorkMode(e.target.value)}>
                      <option value="">Select...</option>
                      <option value="Remote">Remote</option>
                      <option value="Hybrid">Hybrid</option>
                      <option value="Onsite">Onsite</option>
                    </select>
                  </div>
                </div>

                <button type="submit" className="btn-submit-gh" disabled={loading}>
                  {loading ? (
                    <>
                      <div className="spinner-small"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                        <polyline points="17 21 17 13 7 13 7 21"></polyline>
                        <polyline points="7 3 7 8 15 8"></polyline>
                      </svg>
                      Create Job Posting
                    </>
                  )}
                </button>
              </form>
            </div>
          )}

          {/* UPLOAD RESUMES TAB */}
          {activeTab === 'uploadResumes' && (
            <div className="tab-content-section">
              <div className="section-header">
                <h2>Upload & Analyze Resumes</h2>
                <p className="section-description">Upload candidate resumes for AI-powered analysis</p>
              </div>

              {currentStep === 'upload' && (
                <div className="upload-container-gh">
                  <div className="form-field">
                    <label>Select Job *</label>
                    <JobSearchDropdown
                      jobs={myJobs}
                      selectedJobId={selectedJob?.id}
                      onSelectJob={handleJobSelect}
                      placeholder="Search jobs by title, company, location, or ID..."
                    />
                    {selectedJob && (
                      <div style={{marginTop: '10px', padding: '10px', background: '#f0fdf4', borderRadius: '8px', color: '#166534'}}>
                        ✓ Selected: {selectedJob.title} (ID: {selectedJob.id})
                      </div>
                    )}
                  </div>

                  <div className="file-upload-gh">
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.docx"
                      onChange={handleFileSelect}
                      id="file-upload"
                      className="file-input-hidden"
                    />
                    <label htmlFor="file-upload" className="file-upload-label-gh">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="17 8 12 3 7 8"></polyline>
                        <line x1="12" y1="3" x2="12" y2="15"></line>
                      </svg>
                      <span className="upload-text">Click to upload resumes</span>
                      <span className="upload-subtext">PDF or DOCX files accepted</span>
                    </label>
                  </div>

                  {selectedFiles.length > 0 && (
                    <div className="selected-files-gh">
                      <p className="files-count">{selectedFiles.length} file(s) selected:</p>
                      <ul>
                        {selectedFiles.map((file, idx) => (
                          <li key={idx}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
                              <polyline points="13 2 13 9 20 9"></polyline>
                            </svg>
                            {file.name}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <button
                    onClick={handleExtractResumes}
                    disabled={selectedFiles.length === 0 || !jobId || isExtracting}
                    className="btn-submit-gh"
                  >
                    {isExtracting ? (
                      <>
                        <div className="spinner-small"></div>
                        Extracting Information...
                      </>
                    ) : (
                      <>
                        Next: Extract Information
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                          <polyline points="12 5 19 12 12 19"></polyline>
                        </svg>
                      </>
                    )}
                  </button>
                </div>
              )}

              {currentStep === 'extracted' && extractedData && (
                <div className="review-container-gh">
                  <p className="review-instructions">Review and edit the extracted candidate information:</p>

                  <div className="candidates-table-gh">
                    {editedData.map((candidate, idx) => (
                      <div key={idx} className="candidate-row-gh">
                        <div className="candidate-file">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
                            <polyline points="13 2 13 9 20 9"></polyline>
                          </svg>
                          <span>{candidate.fileName}</span>
                        </div>
                        <div className="candidate-inputs">
                          <input
                            type="text"
                            value={candidate.name}
                            onChange={(e) => handleEditData(idx, 'name', e.target.value)}
                            placeholder="Candidate name"
                            disabled={!!candidate.error}
                          />
                          <input
                            type="email"
                            value={candidate.email}
                            onChange={(e) => handleEditData(idx, 'email', e.target.value)}
                            placeholder="email@example.com"
                            disabled={!!candidate.error}
                          />
                        </div>
                        {candidate.error && (
                          <div className="error-note-gh">⚠️ {candidate.error}</div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="button-row-gh">
                    <button onClick={handleResetUpload} className="btn-ghost">
                      Cancel
                    </button>
                    <button
                      onClick={handleProcessResumes}
                      disabled={
                        isProcessing ||
                        editedData.some(c => !c.name.trim() || !c.email.trim() || c.error)
                      }
                      className="btn-submit-gh"
                    >
                      {isProcessing ? (
                        <>
                          <div className="spinner-small"></div>
                          Processing...
                        </>
                      ) : (
                        <>
                          Upload & Analyze
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="9 11 12 14 22 4"></polyline>
                            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                          </svg>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {currentStep === 'processed' && (
                <div className="success-container-gh">
                  <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                  <h3>Processing Complete!</h3>
                  <p>{uploadSuccessMessage}</p>
                  <div className="button-row-gh">
                    <button onClick={handleResetUpload} className="btn-ghost">
                      Upload More Resumes
                    </button>
                    <button
                      onClick={() => {
                        setActiveTab('viewRankings');
                        setRankingsJobId(jobId);
                      }}
                      className="btn-primary-gh"
                    >
                      View Rankings
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                        <polyline points="12 5 19 12 12 19"></polyline>
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* VIEW RANKINGS TAB */}
          {activeTab === 'viewRankings' && (
            <div className="tab-content-section">
              <div className="section-header">
                <h2>Candidate Rankings</h2>
                <p className="section-description">AI-powered candidate evaluation and ranking</p>
              </div>

              <form onSubmit={handleViewRankings} className="form-greenhouse">
                <div className="form-field">
                  <label>Select Job *</label>
                  <JobSearchDropdown
                    jobs={myJobs.filter(job => job.candidateCount > 0)}
                    selectedJobId={rankingsSelectedJob?.id}
                    onSelectJob={handleRankingsJobSelect}
                    placeholder="Search jobs by title, company, location, or ID..."
                  />
                  {rankingsSelectedJob && (
                    <div style={{marginTop: '10px', padding: '10px', background: '#f0fdf4', borderRadius: '8px', color: '#166534'}}>
                      ✓ Selected: {rankingsSelectedJob.title} ({rankingsSelectedJob.candidateCount} candidates)
                    </div>
                  )}
                </div>

                <button type="submit" className="btn-submit-gh" disabled={loading}>
                  {loading ? (
                    <>
                      <div className="spinner-small"></div>
                      Loading Rankings...
                    </>
                  ) : (
                    <>
                      Get Rankings
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 3v18h18"></path>
                        <path d="m19 9-5 5-4-4-3 3"></path>
                      </svg>
                    </>
                  )}
                </button>
              </form>

              {rankings && (
                <div className="rankings-section-gh">
                  <div className="rankings-summary">
                    <h3>{rankings.jobTitle} at {rankings.company}</h3>
                    <div className="stats-row-gh">
                      <div className="stat-pill-gh">
                        <span className="pill-value">{rankings.totalCandidates}</span>
                        <span className="pill-label">Total</span>
                      </div>
                      <div className="stat-pill-gh high">
                        <span className="pill-value">{rankings.statistics?.highFit || 0}</span>
                        <span className="pill-label">High Fit</span>
                      </div>
                      <div className="stat-pill-gh medium">
                        <span className="pill-value">{rankings.statistics?.mediumFit || 0}</span>
                        <span className="pill-label">Medium Fit</span>
                      </div>
                      <div className="stat-pill-gh low">
                        <span className="pill-value">{rankings.statistics?.lowFit || 0}</span>
                        <span className="pill-label">Low Fit</span>
                      </div>
                    </div>
                  </div>

                  <div className="rankings-list-gh">
                    {rankings.candidates.map((candidate, index) => (
                      <div key={index} className="candidate-card-gh">
                        <div className="candidate-header-gh">
                          <div className="rank-badge">#{index + 1}</div>
                          <div className="candidate-info-gh">
                            <h4>{candidate.name}</h4>
                            <p>{candidate.email}</p>
                          </div>
                          <div className="fit-score-gh" style={{color: getFitColor(candidate.fitPercentage)}}>
                            {candidate.fitPercentage}%
                          </div>
                        </div>

                        {candidate.overallAssessment && (
                          <div className="assessment-box">
                            <strong>📋 Overall Assessment</strong>
                            <p>{candidate.overallAssessment}</p>
                          </div>
                        )}

                        {candidate.keyStrengths && (
                          <div className="strengths-box">
                            <strong>✅ Key Strengths</strong>
                            <p>{candidate.keyStrengths}</p>
                          </div>
                        )}

                        {candidate.potentialConcerns && (
                          <div className="concerns-box">
                            <strong>⚠️ Potential Concerns</strong>
                            <p>{candidate.potentialConcerns}</p>
                          </div>
                        )}

                        {candidate.interviewRecommendations && (
                          <div className="recommendations-box">
                            <strong>💡 Interview Recommendations</strong>
                            <p>{candidate.interviewRecommendations}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        {activeTab === 'company' && isAdmin && (
          <div className="tab-content-section">
            <div className="section-header">
              <h2>⚙️ Company Settings</h2>
              <p className="section-description">
                Manage your company profile, users, and subscription
              </p>
            </div>

            <div className="company-section">
              <CompanyStats />
              <CompanyProfile />
              <CompanySubscription />
              <CompanyUsers />
            </div>
          </div>
        )}
        </div>
      </div>

      <>
      {/* EDIT JOB MODAL */}
      {showEditModal && (
        <div className="modal-overlay" onClick={handleCloseEditModal}>
          <div className="modal-content-edit" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-edit">
              <h2>Edit Job Posting</h2>
              <button className="close-btn" onClick={handleCloseEditModal}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            {error && (
              <div className="error-banner-edit">
                {error}
              </div>
            )}

            <form onSubmit={handleUpdateJob} className="edit-job-form">
              <div className="form-grid-2">
                <div className="form-field">
                  <label>Job Title *</label>
                  <input
                    type="text"
                    value={editJobTitle}
                    onChange={(e) => setEditJobTitle(e.target.value)}
                    placeholder="e.g., Senior Java Developer"
                    required
                  />
                </div>

                <div className="form-field">
                  <label>Company *</label>
                  <input
                    type="text"
                    value={editCompany}
                    onChange={(e) => setEditCompany(e.target.value)}
                    placeholder="e.g., Tech Corp Inc"
                    required
                  />
                </div>
              </div>

              <div className="form-field">
                <label>Job Description *</label>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder="Enter the job description..."
                  rows="6"
                  required
                />
              </div>

              <div className="form-field">
                <label>Required Qualifications (comma-separated)</label>
                <input
                  type="text"
                  value={editQualifications}
                  onChange={(e) => setEditQualifications(e.target.value)}
                  placeholder="e.g., Java, Spring Boot, Microservices"
                />
              </div>

              <div className="form-grid-3">
                <div className="form-field">
                  <label>Years of Experience</label>
                  <input
                    type="text"
                    value={editExperience}
                    onChange={(e) => setEditExperience(e.target.value)}
                    placeholder="e.g., 5-8 years"
                  />
                </div>

                <div className="form-field">
                  <label>Location</label>
                  <input
                    type="text"
                    value={editLocation}
                    onChange={(e) => setEditLocation(e.target.value)}
                    placeholder="e.g., San Francisco, CA"
                  />
                </div>

                <div className="form-field">
                  <label>Work Mode</label>
                  <select
                    value={editWorkMode}
                    onChange={(e) => setEditWorkMode(e.target.value)}
                  >
                    <option value="">Select...</option>
                    <option value="Remote">Remote</option>
                    <option value="Hybrid">Hybrid</option>
                    <option value="Onsite">Onsite</option>
                  </select>
                </div>
              </div>

              <div className="modal-actions">
                {editingJob && editingJob.candidateCount > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      handleCloseEditModal();
                      handleReanalyzeCandidates(editingJob.id);
                    }}
                    className="btn-reanalyze"
                    disabled={isUpdating}
                    style={{
                      marginRight: 'auto',
                      backgroundColor: '#f59e0b',
                      color: 'white',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    🔄 Re-analyze Candidates ({editingJob.candidateCount})
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleCloseEditModal}
                  className="btn-cancel"
                  disabled={isUpdating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-save"
                  disabled={isUpdating}
                >
                  {isUpdating ? 'Updating...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* RE-ANALYZING PROGRESS */}
            {isReanalyzing && (
              <div className="modal-overlay">
                <div className="modal-content-edit" style={{maxWidth: '500px', textAlign: 'center', padding: '40px'}}>
                  <h2>🔄 Re-analyzing Candidates...</h2>
                  <p style={{margin: '20px 0', color: '#666'}}>{reanalyzeProgress}</p>
                  <div style={{
                    width: '100%',
                    height: '4px',
                    backgroundColor: '#e5e7eb',
                    borderRadius: '2px',
                    overflow: 'hidden',
                    margin: '20px 0'
                  }}>
                    <div style={{
                      width: '100%',
                      height: '100%',
                      backgroundColor: '#059669',
                      animation: 'progress 2s ease-in-out infinite'
                    }}></div>
                  </div>
                  <p style={{fontSize: '14px', color: '#999'}}>This may take several minutes depending on the number of candidates...</p>
                </div>
              </div>
            )}
      </>
    </div>
    </>
  );
}

export default AdminPanel;